/**
 * Conversations (docs/API.md "Conversations", docs/CONVERSATIONS.md, DOMAIN.md "conversations"): threads between a
 * user and their agents, messages with a dense per-thread `seq`, approvals for every agent action that would move
 * money or change a deployment or permission, and per-user memory facts with provenance. Owner-only; agents'
 * public pages never expose any of it.
 */
import { z } from 'zod'
import { Cursor, EvidenceId, IsoTimestamp, MicroString, UsdString, Uuid } from '../primitives.js'
import { AgentSummary, JsonObject } from './common.js'
import { Venue } from '../enums.js'

// Enumerations (text columns with CHECKs in schema `conversations`) -------------------------------------------------

export const ThreadKind = z.enum(['desk', 'agent', 'support'])
export type ThreadKind = z.output<typeof ThreadKind>
export const ThreadStatus = z.enum(['open', 'archived'])
export type ThreadStatus = z.output<typeof ThreadStatus>
export const MessageRole = z.enum(['user', 'agent', 'system', 'tool'])
export type MessageRole = z.output<typeof MessageRole>
export const MessageKind = z.enum(['text', 'proposal', 'approval', 'report', 'alert', 'evidence', 'tool_call', 'tool_result', 'status'])
export type MessageKind = z.output<typeof MessageKind>
export const ApprovalKind = z.enum(['order', 'deployment', 'permission_change', 'research', 'launch'])
export type ApprovalKind = z.output<typeof ApprovalKind>
export const ApprovalState = z.enum(['pending', 'approved', 'rejected', 'expired', 'executed', 'failed'])
export type ApprovalState = z.output<typeof ApprovalState>
/** Who decided: the user, a standing rule the user set (`rule.auto_approve.*`), or the system (expiry). */
export const ApprovalSource = z.enum(['user', 'rule', 'system'])
export type ApprovalSource = z.output<typeof ApprovalSource>
export const MemorySource = z.enum(['user', 'agent', 'system'])
export type MemorySource = z.output<typeof MemorySource>

/** Approval kinds a standing rule may auto-approve; deployments, permission changes and launches always need the user. */
export const AUTO_APPROVABLE_KINDS = ['order', 'research'] as const satisfies readonly ApprovalKind[]
/** Default lifetime of a pending approval. */
export const APPROVAL_DEFAULT_TTL_MS = 15 * 60_000
/** Longest lifetime a proposal may ask for. */
export const APPROVAL_MAX_TTL_MS = 7 * 24 * 3600_000
/** User messages per user per minute (`POST /threads/:id/messages`). */
export const THREAD_MESSAGES_PER_MINUTE = 30
export const THREAD_MESSAGE_MAX_CHARS = 4000
export const THREAD_TITLE_MAX_CHARS = 200

// Wire shapes --------------------------------------------------------------------------------------------------------

/** `thread_state.budget`: model credits per day for this thread's agent turns and what today's turns spent. */
export const ThreadBudget = z.object({
  creditsPerDayMicro: MicroString.nullable(),
  spentMicro: MicroString,
  /** UTC day (`YYYY-MM-DD`) `spentMicro` counts; null before the first spend. */
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
})
export type ThreadBudget = z.output<typeof ThreadBudget>

export const ThreadWorkState = z.object({
  goals: z.array(z.unknown()),
  activeAgents: z.array(Uuid),
  budget: ThreadBudget,
  /** The highest message seq the agents' turns have consumed. */
  cursor: z.number().int().nonnegative(),
  updatedAt: IsoTimestamp,
})
export type ThreadWorkState = z.output<typeof ThreadWorkState>

export const Thread = z.object({
  id: Uuid,
  kind: ThreadKind,
  /** The agent of an `agent` thread. */
  agent: AgentSummary.nullable(),
  title: z.string(),
  state: ThreadStatus,
  /** The newest message's seq (0 when empty). */
  lastSeq: z.number().int().nonnegative(),
  /** Approvals waiting for the user's decision. */
  pendingApprovals: z.number().int().nonnegative(),
  lastMessageAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
})
export type Thread = z.output<typeof Thread>

export const Approval = z.object({
  id: Uuid,
  threadId: Uuid,
  messageId: Uuid,
  kind: ApprovalKind,
  payload: JsonObject,
  state: ApprovalState,
  expiresAt: IsoTimestamp,
  decidedAt: IsoTimestamp.nullable(),
  decidedBy: Uuid.nullable(),
  source: ApprovalSource.nullable(),
  /** The standing rule (memory fact id) that auto-approved it. */
  ruleId: Uuid.nullable(),
  /** The execution outcome (`executed` / `failed`), written by the runtime. */
  result: JsonObject.nullable(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
})
export type Approval = z.output<typeof Approval>

export const ThreadDetail = Thread.extend({
  workState: ThreadWorkState,
  /** Pending approvals, oldest first. */
  approvals: z.array(Approval),
})
export type ThreadDetail = z.output<typeof ThreadDetail>

export const Message = z.object({
  id: Uuid,
  threadId: Uuid,
  seq: z.number().int().positive(),
  role: MessageRole,
  agentId: Uuid.nullable(),
  kind: MessageKind,
  body: JsonObject,
  evidenceIds: z.array(EvidenceId),
  createdAt: IsoTimestamp,
  /** A `proposal`'s approval in its current state; null for other kinds. */
  approval: Approval.nullable(),
})
export type Message = z.output<typeof Message>

export const MemoryFact = z.object({
  id: Uuid,
  key: z.string(),
  value: z.unknown(),
  source: MemorySource,
  threadId: Uuid.nullable(),
  evidenceId: EvidenceId.nullable(),
  validFrom: IsoTimestamp,
  validTo: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
})
export type MemoryFact = z.output<typeof MemoryFact>

/** Memory keys: lower case, `[a-z0-9._:-]`, at most 128 characters. */
export const MemoryKey = z.string().regex(/^[a-z0-9][a-z0-9._:-]{0,127}$/, 'memory key: 1-128 of a-z, 0-9, . _ : -')
export type MemoryKey = z.output<typeof MemoryKey>

/**
 * A standing rule (`rule.auto_approve.<kind>` for `order` or `research`, set by the user): proposals of that kind
 * within these bounds are approved without asking, recorded as `source: rule`, `decidedBy` the user. An order needs
 * `payload.amountUsd` ≤ `maxAmountUsd`, research `payload.creditsMicro` ≤ `maxCreditsMicro`; a payload without the
 * amount never matches. `agentIds` limits the rule to those agents; `expiresAt` ends it.
 */
export const StandingRule = z
  .object({
    maxAmountUsd: UsdString.refine((s) => !s.startsWith('-'), 'must not be negative').optional(),
    maxCreditsMicro: MicroString.refine((s) => !s.startsWith('-'), 'must not be negative').optional(),
    agentIds: z.array(Uuid).min(1).max(50).optional(),
    expiresAt: IsoTimestamp.optional(),
  })
  .strict()
export type StandingRule = z.output<typeof StandingRule>
export const STANDING_RULE_PREFIX = 'rule.auto_approve.'

// Requests -----------------------------------------------------------------------------------------------------------

export const ThreadParams = z.object({ id: Uuid })
export const ApprovalParams = z.object({ id: Uuid })
export const MemoryParams = z.object({ key: MemoryKey })

export const ListThreadsQuery = z.object({
  state: z.enum(['open', 'archived', 'all']).default('open'),
  kind: ThreadKind.optional(),
  cursor: Cursor.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
})

export const CreateThreadBody = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('desk'), title: z.string().trim().min(1).max(THREAD_TITLE_MAX_CHARS).optional() }).strict(),
  z.object({ kind: z.literal('agent'), agentId: Uuid, title: z.string().trim().min(1).max(THREAD_TITLE_MAX_CHARS).optional() }).strict(),
  z.object({ kind: z.literal('support'), title: z.string().trim().min(1).max(THREAD_TITLE_MAX_CHARS).optional() }).strict(),
])
export type CreateThreadBody = z.output<typeof CreateThreadBody>

export const PatchThreadBody = z
  .object({
    title: z.string().trim().min(1).max(THREAD_TITLE_MAX_CHARS).optional(),
    state: ThreadStatus.optional(),
  })
  .strict()
  .refine((b) => b.title !== undefined || b.state !== undefined, 'nothing to change')
export type PatchThreadBody = z.output<typeof PatchThreadBody>

export const ThreadMessagesQuery = z.object({
  /** Messages with seq greater than this (0 = from the start). */
  after: z.coerce.number().int().nonnegative().default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

/**
 * `confirmMemory` / `declineMemory`: the id of a memory proposal (a `status` message of the same thread) the user
 * answers with this message; stored in the message body as given, so the turn (lane C2) writes or drops the fact
 * without a model call. At most one of the two.
 */
export const PostMessageBody = z
  .object({
    text: z.string().trim().min(1).max(THREAD_MESSAGE_MAX_CHARS),
    confirmMemory: Uuid.optional(),
    declineMemory: Uuid.optional(),
    /**
     * The venue a client picked for this request (the web sends none; a new agent's chain is chosen in /create): stored in the body and carried into an
     * authoring intent's context; an agent thread always uses the agent's venue of record.
     */
    venue: Venue.optional(),
  })
  .strict()
  .refine((b) => !(b.confirmMemory && b.declineMemory), 'confirmMemory and declineMemory exclude each other')
export type PostMessageBody = z.output<typeof PostMessageBody>

export const ThreadStreamQuery = z.object({
  /** Resume after this seq (the SSE `Last-Event-ID` header wins when both are given). */
  after: z.coerce.number().int().nonnegative().optional(),
})

export const DecideApprovalBody = z.object({ reason: z.string().trim().min(1).max(500).optional() }).strict()
export type DecideApprovalBody = z.output<typeof DecideApprovalBody>

export const MyMemoryQuery = z.object({
  /** Only keys starting with this. */
  prefix: z.string().regex(/^[a-z0-9._:-]{1,128}$/).optional(),
  /** Include closed facts (history), newest first per key. */
  history: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
})

export const PutMemoryBody = z.object({ value: z.unknown().refine((v) => v !== undefined, 'value is required'), threadId: Uuid.optional() }).strict()
export type PutMemoryBody = z.output<typeof PutMemoryBody>

// Responses ----------------------------------------------------------------------------------------------------------

export const ThreadPage = z.object({ items: z.array(Thread), nextCursor: Cursor.nullable() })
export type ThreadPage = z.output<typeof ThreadPage>

export const MessagePage = z.object({
  items: z.array(Message),
  /** Pass as `after` for the next page; null when this page reached `lastSeq`. */
  nextAfter: z.number().int().nonnegative().nullable(),
  lastSeq: z.number().int().nonnegative(),
})
export type MessagePage = z.output<typeof MessagePage>

export const MemoryFacts = z.object({ items: z.array(MemoryFact) })
export type MemoryFacts = z.output<typeof MemoryFacts>

export const MemoryClosed = z.object({ ok: z.literal(true), closed: z.boolean() })
export type MemoryClosed = z.output<typeof MemoryClosed>

/**
 * One event of `GET /threads/:id/stream`. SSE `event:` names the variant; `id:` is always the highest message seq
 * delivered so far, so `Last-Event-ID` resumes the message sequence.
 */
export const ThreadStreamEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('message'), message: Message }),
  z.object({ type: z.literal('approval'), approval: Approval }),
  z.object({ type: z.literal('state'), thread: Thread, workState: ThreadWorkState }),
])
export type ThreadStreamEvent = z.output<typeof ThreadStreamEvent>

// Job payloads (docs/LANES.md "Job kinds and payloads"; C2 and C4 implement the handlers) ----------------------------

/** `conversation.turn`: the agents answer the thread from `thread_state.cursor` up to `seq`. */
export const ConversationTurnPayload = z.object({ threadId: Uuid, userId: Uuid, messageId: Uuid, seq: z.number().int().positive() })
export type ConversationTurnPayload = z.output<typeof ConversationTurnPayload>
/** `conversation.approval.execute`: carry out an `approved` approval through the existing runtime paths. */
export const ConversationApprovalExecutePayload = z.object({ approvalId: Uuid, threadId: Uuid, userId: Uuid })
export type ConversationApprovalExecutePayload = z.output<typeof ConversationApprovalExecutePayload>
/** One event of `conversation.alert` by reference (lane C4, docs/RUNTIME.md "Alerts and reports"); the handler loads the rows. */
export const ConversationAlertEvent = z.discriminatedUnion('type', [
  // The execution follower's evidence (lane BOO2): a platform fill, an order that ended without one, a skipped signal.
  z.object({ type: z.literal('fill'), evidenceId: EvidenceId }),
  z.object({ type: z.literal('refusal'), evidenceId: EvidenceId }),
  z.object({ type: z.literal('skipped'), source: z.enum(['copy', 'snipe', 'schedule']), evidenceId: EvidenceId }),
  z.object({ type: z.literal('protective'), evidenceId: EvidenceId }),
  z.object({ type: z.literal('permission'), permissionId: Uuid, change: z.enum(['expiring', 'revoked', 'paused', 'active']) }),
  z.object({ type: z.literal('flow'), evidenceId: EvidenceId }),
  z.object({ type: z.literal('research'), inboxId: Uuid }),
  z.object({ type: z.literal('milestone'), agentId: Uuid, milestone: z.string().min(1).max(100) }),
])
export type ConversationAlertEvent = z.output<typeof ConversationAlertEvent>
/** `conversation.alert` for one event (the platform schedule runs it with `{}`: the scan). */
export const ConversationAlertPayload = z.object({ userId: Uuid, agentId: Uuid.nullable().optional(), event: ConversationAlertEvent })
export type ConversationAlertPayload = z.output<typeof ConversationAlertPayload>
